import { MarkedExtension } from 'marked';

/**
 * Docs base URL patterns to detect and transform.
 * Links matching these patterns will be converted to open in the docs popup
 * instead of navigating to a new tab.
 */
const DOCS_BASE_URL = 'https://docs.zerops.io/';
const DOCS_BASE_URL_ALT = 'http://docs.zerops.io/';

/** Exported for use by externalLinksExtension to skip docs links */
export const DOCS_BASE_URLS = [ DOCS_BASE_URL, DOCS_BASE_URL_ALT ] as const;

/**
 * Marked extension that transforms docs.zerops.io links into popup-enabled links.
 * Matches the visual style of the zui-docs-text component.
 *
 * Input markdown:
 *   [DNS config](https://docs.zerops.io/references/networking/dns)
 *
 * Output HTML:
 *   <a href="https://docs.zerops.io/references/networking/dns"
 *      data-zui-new-docs="references/networking/dns"
 *      class="markdown-docs-link">DNS <span>config</span></a>
 *
 * The last word is wrapped in a <span> to position the help icon,
 * matching the docs-text component behavior.
 *
 * The `data-zui-new-docs` attribute is picked up by the markdown-docs-handler
 * directive to intercept clicks and open the docs popup instead.
 */
export function docsLinksExtension(): MarkedExtension {
  return {
    renderer: {
      link(href: string, title: string | null | undefined, text: string): string | false {
        // Check if this is a docs.zerops.io link
        const isDocsLink = href.startsWith(DOCS_BASE_URL) || href.startsWith(DOCS_BASE_URL_ALT);

        if (!isDocsLink) {
          // Return false to let other extensions/default renderer handle it
          return false;
        }

        // Extract the path after the base URL
        let docsPath: string;
        if (href.startsWith(DOCS_BASE_URL)) {
          docsPath = href.slice(DOCS_BASE_URL.length);
        } else {
          docsPath = href.slice(DOCS_BASE_URL_ALT.length);
        }

        // Remove leading slash if present (normalize)
        if (docsPath.startsWith('/')) {
          docsPath = docsPath.slice(1);
        }

        // Handle hash fragments - keep them in the docs path
        // e.g., "references/cli#vpn" stays as-is

        const titleAttr = title ? ` title="${escapeHtml(title)}"` : '';

        // Note: text content is NOT escaped here because marked already processes
        // inline elements (like <code>) before passing to the renderer. Escaping
        // would break legitimate HTML like <code>zsc scale</code>.

        // Wrap the last word in a <span> to match docs-text component styling
        // This allows positioning the help icon after the last word
        const words = text.split(' ');
        const lastWord = words.pop() || '';
        const formattedText = words.length > 0
          ? `${words.join(' ')} <span>${lastWord}</span>`
          : `<span>${lastWord}</span>`;

        // Keep the original href for accessibility/fallback, but add data attribute
        // for the directive to intercept
        return `<a href="${escapeHtml(href)}"${titleAttr} data-zui-new-docs="${escapeHtml(docsPath)}" class="markdown-docs-link">${formattedText}</a>`;
      }
    }
  };
}

/**
 * Simple HTML escape for attribute values
 */
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
